import React, { useEffect, useState } from 'react'
import '../full.css'
import './RedeemPurchase.css'
import { Navigate } from 'react-router-dom'
import Moment from 'react-moment'
import { useParams } from 'react-router-dom'

// The purchase id and security key in the URL are the credentials. They go to
// the backend in a request body rather than a GraphQL filter, so purchases no
// longer have to be publicly readable, and the zip is served by an endpoint
// that re-checks them instead of straight from /uploads.
const REDEEM_URL = `${process.env.REACT_APP_API_ENDPOINT}/api/purchases/redeem`
const DOWNLOAD_URL = `${process.env.REACT_APP_API_ENDPOINT}/api/purchases/download`

const SUPPORT_EMAIL = 'admin@visceralsounds.com'

const RedeemPurchase = () => {
  const { purchase_id, security_key } = useParams()
  const [state, setState] = useState({ status: 'loading' })
  const [downloadError, setDownloadError] = useState(null)

  useEffect(() => {
    if (!purchase_id || !security_key) {
      setState({ status: 'missing' })
      return undefined
    }

    let cancelled = false

    const load = async () => {
      try {
        const response = await fetch(REDEEM_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ purchase_id, security_key }),
        })

        if (response.status === 410) {
          if (!cancelled) setState({ status: 'expired' })
          return
        }

        if (!response.ok) {
          if (!cancelled) setState({ status: 'missing' })
          return
        }

        const purchase = await response.json()
        if (!cancelled) setState({ status: 'ready', purchase })
      } catch (error) {
        if (!cancelled) setState({ status: 'error' })
      }
    }

    load()

    return () => {
      cancelled = true
    }
  }, [purchase_id, security_key])

  const downloadFile = async (e) => {
    e.preventDefault()
    setDownloadError(null)

    try {
      const response = await fetch(DOWNLOAD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ purchase_id, security_key }),
      })

      if (!response.ok) {
        setDownloadError("We couldn't start your download. Please try again in a moment.")
        return
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = state.purchase.filename
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      setDownloadError("We couldn't start your download. Please try again in a moment.")
    }
  }

  if (state.status === 'loading') return <h4>...loading</h4>

  // No such purchase, or the key doesn't match — same treatment as before.
  if (state.status === 'missing') return <Navigate to="/" />

  if (state.status === 'expired' || state.status === 'error') {
    return (
      <div id="redeem-purchase" style={{ padding: '1 0px' }}>
        <div
          className="row patches-row"
          style={{ minHeight: '100%', backgroundColor: 'transparent' }}
        >
          <div className="large-10 small-12 large-centered columns">
            <div style={{ color: 'white' }} className="purchaseContainer">
              <h2 style={{ color: 'white' }}>
                {state.status === 'expired'
                  ? 'This download link has expired'
                  : "We couldn't load your purchase"}
              </h2>
              <p>
                {state.status === 'expired'
                  ? 'Download links stay active for 30 days.'
                  : 'Something went wrong on our side.'}{' '}
                Drop us a message at <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> and
                we'll be glad to help you get your patches.
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const purchase = state.purchase

  return (
    <div id="redeem-purchase" style={{ padding: '1 0px' }}>
      <div
        className="row patches-row"
        style={{ minHeight: '100%', backgroundColor: 'transparent' }}
      >
        <div className="large-10 small-12 large-centered columns">
          <div style={{ color: 'white' }} className="purchaseContainer">
            <h2 style={{ color: 'white' }}>Thank you for purchasing {purchase.filename}</h2>
            <p>
              Please use your personal download link below.
              <br />
              Download your patch pack and store it somewhere safe, this link will remain active for
              30 days.
              <br />
              In the unfortunate event that you lose your stored pack, just drop us a message using
              the contact form.
            </p>
            <div
              className="small-12 small-centered large-centered columns"
              style={{ marginBottom: '2em' }}
            >
              <div className="large-4 small-12  columns">
                {purchase.cover_url ? (
                  <img
                    alt="patch set cover"
                    src={process.env.REACT_APP_API_ENDPOINT + purchase.cover_url}
                  />
                ) : null}
              </div>

              <table style={{ margin: 'auto' }}>
                <tbody>
                  <tr>
                    <td>
                      <span className="label">Invoice Number:</span>
                    </td>
                    <td>{purchase.id}</td>
                  </tr>

                  <tr>
                    <td>
                      <span className="label">Purchase date:</span>
                    </td>
                    <td>
                      <Moment format="ddd, MMM Do YYYY. hh:mm A">{purchase.created_at}</Moment>
                    </td>
                  </tr>

                  <tr>
                    <td>
                      <span className="label">Download Link:</span>
                    </td>
                    <td>
                      <a
                        rel="noopener noreferrer"
                        href={`/redeem-purchase/${purchase_id}/${security_key}`}
                        style={{ fontWeight: 'bold' }}
                        onClick={downloadFile}
                      >
                        Download your patches
                      </a>
                    </td>
                  </tr>

                  <tr>
                    <td>
                      <span className="label">Expiration date:</span>
                    </td>
                    <td>
                      <Moment format="ddd, MMM Do YYYY. hh:mm A">{purchase.expiration_date}</Moment>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            {downloadError ? <p style={{ color: '#D39D60' }}>{downloadError}</p> : null}
          </div>
        </div>
      </div>
    </div>
  )
}

export default RedeemPurchase
